import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../../context/ThemeContext';

interface ContactModalProps {
  isOpen: boolean;
  type: 'demo' | 'start';
  onClose: () => void;
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const FORM_URLS = {
  demo: 'https://readdy.ai/api/form/d743l3ecsbv12mkucupg',
  start: 'https://readdy.ai/api/form/d743krmcsbv12mkucup0',
};

const industries = [
  'Home Services & Trades', 'Law Firms', 'Wellness & Lifestyle Care',
  'Insurance Advisory & Brokerage', 'Residential Real Estate', 'Other',
];

export default function ContactModal({ isOpen, type, onClose }: ContactModalProps) {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const { isDark } = useTheme();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setStatus('idle');
      setCharCount(0);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const title = type === 'demo' ? 'Schedule a Demo' : 'Get Started';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const messageEl = e.currentTarget.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
    if (messageEl && messageEl.value.length > 500) return;
    setStatus('submitting');
    const formData = new FormData(e.currentTarget);
    const data = new URLSearchParams();
    formData.forEach((v, k) => { if (typeof v === 'string') data.append(k, v); });
    try {
      const res = await fetch(FORM_URLS[type], {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('success'); formRef.current?.reset(); setCharCount(0); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  const inputCls = `w-full px-4 py-3 rounded-xl text-sm transition-colors focus:outline-none ${
    isDark
      ? 'bg-[#0A0A0A] border border-[#1A1A1A] text-[#C9CED6] placeholder-[#444] focus:border-[#FF2B2B]/40'
      : 'bg-[#F5F5F7] border border-[#E8E8ED] text-[#1D1D1F] placeholder-[#aaa] focus:border-[#FF2B2B]/40'
  }`;

  const labelCls = `block text-xs font-semibold tracking-widest uppercase mb-2 ${isDark ? 'text-[#C9CED6]/55' : 'text-[#6E6E73]'}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md cursor-pointer" onClick={onClose}></div>
      <div
        className={`relative z-10 w-full max-w-lg rounded-2xl overflow-hidden ${isDark ? 'bg-[#0D0D0D] border border-[#1A1A1A]' : 'bg-white border border-[#E8E8ED]'}`}
        style={{ boxShadow: isDark ? '0 40px 80px rgba(0,0,0,0.8)' : '0 40px 80px rgba(0,0,0,0.15)' }}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-7 py-5 border-b ${isDark ? 'border-[#1A1A1A]' : 'border-[#E8E8ED]'}`}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF2B2B] animate-glow-pulse inline-block"></span>
              <span className="text-[#FF2B2B] text-xs font-bold tracking-widest uppercase">A Bot Smarter</span>
            </div>
            <h3 className={`font-black text-xl ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`} style={{ letterSpacing: '-0.02em' }}>{title}</h3>
          </div>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-all ${isDark ? 'text-[#C9CED6]/50 hover:bg-[#1A1A1A]' : 'text-[#6E6E73] hover:bg-[#F5F5F7]'}`}>
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-7 py-6 max-h-[70vh] overflow-y-auto">
          {status === 'success' ? (
            <div className="text-center py-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#FF2B2B]/10 mx-auto mb-5">
                <i className="ri-check-line text-[#FF2B2B] text-2xl"></i>
              </div>
              <h4 className={`font-black text-xl mb-3 ${isDark ? 'text-white' : 'text-[#1D1D1F]'}`} style={{ letterSpacing: '-0.02em' }}>We&apos;ll be in touch.</h4>
              <p className={`text-sm leading-relaxed max-w-xs mx-auto mb-6 ${isDark ? 'text-[#C9CED6]/60' : 'text-[#6E6E73]'}`}>
                Thanks for reaching out. A member of our team will contact you within 24 hours.
              </p>
              <button onClick={onClose} className="bg-[#FF2B2B] text-white font-semibold px-8 py-3 rounded-xl cursor-pointer whitespace-nowrap hover:bg-[#e02222] transition-all">
                Close
              </button>
            </div>
          ) : (
            <form id={`contact-form-${type}`} ref={formRef} data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div>
                <label className={labelCls}>Full Name <span className="text-[#FF2B2B]">*</span></label>
                <input type="text" name="name" required placeholder="John Smith" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email Address <span className="text-[#FF2B2B]">*</span></label>
                <input type="email" name="email" required placeholder="john@company.com" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Phone Number</label>
                <input type="tel" name="phone" placeholder="+1 (555) 000-0000" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Industry <span className="text-[#FF2B2B]">*</span></label>
                <select name="industry" required className={`${inputCls} appearance-none cursor-pointer`} defaultValue="">
                  <option value="" disabled>Select your industry</option>
                  {industries.map((ind) => <option key={ind} value={ind}>{ind}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Tell us about your business</label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Briefly describe your current lead management challenges..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className={`${inputCls} resize-none`}
                />
                <div className="text-right mt-1">
                  <span className={`text-xs ${charCount > 480 ? 'text-[#FF2B2B]' : isDark ? 'text-[#444]' : 'text-[#aaa]'}`}>{charCount}/500</span>
                </div>
              </div>
              {status === 'error' && <p className="text-[#FF2B2B] text-sm text-center">Something went wrong. Please try again.</p>}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-[#FF2B2B] hover:bg-[#e02222] disabled:opacity-60 text-white font-bold py-4 rounded-xl text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 transition-all"
                style={{ boxShadow: '0 8px 24px rgba(255,43,43,0.25)' }}
              >
                {status === 'submitting' ? (<><i className="ri-loader-4-line animate-spin"></i><span>Sending...</span></>) : (<><span>{title}</span><i className="ri-arrow-right-line"></i></>)}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
